// IMPORTING NECESSARY FILES
import { useRouteLoaderData } from '@remix-run/react'
import React from 'react'
import { type loader as rootLoader, useOptimisticThemeMode } from '#app/root'
// IMPORTING TYPES
import { type Theme } from '#app/utils/theme.server'

// Declaring the Types required
export type ThemeContextReducerStateType = {
	currentTheme: Theme | 'system'
	nextTheme: Theme | 'system'
}

export type ThemeContextReducerActionType = {
	type: Theme | 'system'
}

export type ThemeContextProviderProps = {
	children: JSX.Element
}

export type ThemeContextType = {
	state: ThemeContextReducerStateType
	dispatch: React.Dispatch<ThemeContextReducerActionType>
}

// Creating the theme context
export const ThemeContext = React.createContext<ThemeContextType | null>(null)

// Creating the themeContextReducer
export function themeContextReducer(
	state: ThemeContextReducerStateType,
	action: ThemeContextReducerActionType,
): ThemeContextReducerStateType {
	switch (action.type) {
		case 'dark':
			return { ...state, currentTheme: 'dark', nextTheme: 'system' }
		case 'light':
			return { ...state, currentTheme: 'light', nextTheme: 'dark' }
		case 'system':
			return { ...state, currentTheme: 'system', nextTheme: 'light' }
		default:
			return state
	}
}

// Creating the ThemeContextProvider
export default function ThemeContextProvider({
	children,
}: ThemeContextProviderProps) {
	// Getting the initial and next themes after the user loads the page
	const rootLoaderdata = useRouteLoaderData<typeof rootLoader>('root loader')
	const optimisticTheme = useOptimisticThemeMode('/')

	const currentTheme =
		optimisticTheme ?? rootLoaderdata?.requestInfo.userPrefs.theme ?? 'system'

	const nextTheme =
		currentTheme === 'system'
			? 'light'
			: currentTheme === 'light'
				? 'dark'
				: 'system'

	// Creating the global state and returning the context
	const [state, dispatch] = React.useReducer(themeContextReducer, {
		currentTheme,
		nextTheme,
	})

	return (
		<ThemeContext.Provider value={{ state, dispatch }}>
			{children}
		</ThemeContext.Provider>
	)
}
